import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { Howl } from "howler";
import { AUDIO_TRACK } from "@/lib/media";

const AudioContext = createContext(null);

const TARGET_VOLUME = 0.35;

export const useAudio = () => useContext(AudioContext);

export const AudioProvider = ({ children }) => {
  const howlRef = useRef(null);
  const pauseTimer = useRef(null);
  const volumeGuard = useRef(null);
  // Zodra de bezoeker zelf op de knop drukt, houdt de automaat op: zette zij
  // het geluid uit, dan mag een scroll het niet weer aanzetten.
  const userDecided = useRef(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const sound = new Howl({
      src: [AUDIO_TRACK],
      loop: true,
      html5: true,
      volume: 0,
      // Weigert de browser de automatische start, dan mag de knop niet
      // blijven staan alsof er muziek speelt — dat is precies de verwarrende
      // toestand van "hij staat aan maar ik hoor niets".
      onplayerror: () => {
        setPlaying(false);
        sound.once('unlock', () => {
          if (!userDecided.current) {
            sound.play();
            sound.fade(0, TARGET_VOLUME, 1500);
            setPlaying(true);
          }
        });
      },
    });
    howlRef.current = sound;
    return () => {
      sound.unload();
    };
  }, []);

  const fadeTo = useCallback((target, ms = 1200) => {
    const s = howlRef.current;
    if (!s) return;
    const from = s.volume();
    s.fade(from, target, ms);
  }, []);

  const play = useCallback(() => {
    const s = howlRef.current;
    if (!s) return;
    // Een nog lopende pause-timer zou de muziek die we hier starten 820 ms
    // later alsnog stilzetten. Eerst afbreken.
    clearTimeout(pauseTimer.current);

    // Faden pas zodra het geluid ECHT speelt. Bij html5:true bestaat het
    // audio-element nog niet op het moment van play(), en laat Howler een
    // fade die daarvoor wordt aangeroepen vallen — het volume bleef dan op 0
    // staan: technisch "playing", maar onhoorbaar.
    const fadeIn = () => {
      s.fade(s.volume(), TARGET_VOLUME, 1500);
      // Vangnet: slaat de fade alsnog niet aan, zet het volume dan hard.
      clearTimeout(volumeGuard.current);
      volumeGuard.current = setTimeout(() => {
        if (s.volume() < 0.05) s.volume(TARGET_VOLUME);
      }, 1700);
    };

    if (s.playing()) {
      fadeIn();
    } else {
      s.once('play', fadeIn);
      s.play();
    }
    setPlaying(true);
  }, []);

  const pause = useCallback(() => {
    const s = howlRef.current;
    if (!s) return;
    clearTimeout(volumeGuard.current); // anders zet het vangnet 'm zo weer aan
    fadeTo(0, 800);
    clearTimeout(pauseTimer.current);
    pauseTimer.current = setTimeout(() => s.pause(), 820);
    setPlaying(false);
  }, [fadeTo]);

  const toggle = useCallback(() => {
    userDecided.current = true;
    if (playing) pause();
    else play();
  }, [playing, play, pause]);

  // Geen luisteraars die stiekem proberen te starten: het intro-scherm vraagt
  // om één klik ("Enter"), en díe klik is de toestemming die browsers eisen
  // voor geluid. Zie EntryScreen — dat roept play() aan.

  // Timers opruimen bij unmount.
  useEffect(() => () => {
    clearTimeout(pauseTimer.current);
    clearTimeout(volumeGuard.current);
  }, []);

  return (
    <AudioContext.Provider value={{ playing, toggle, play, pause, fadeTo }}>
      {children}
    </AudioContext.Provider>
  );
};
